import type { ApiKey, ApiKeyMode, CreatedApiKey } from "@aiyo/shared";
import { Key, Plus } from "@gravity-ui/icons";
import { Alert, Button, Card, Table, toast } from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, FormDialog, InfoDialog } from "../../components/dialogs";
import { SelectInput, TextInput } from "../../components/fields";
import { Code, CodeBlock, EmptyBlock, ErrorBlock, LoadingBlock, StateChip } from "../../components/ui";
import { useFormat } from "../../lib/format";
import { useApiKeys, useCreateApiKey, useRevokeApiKey } from "../../lib/queries";

export function KeysTab({ appId, canEdit }: { appId: string; canEdit: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const keys = useApiKeys(appId);
  const revoke = useRevokeApiKey(appId);
  const [createOpen, setCreateOpen] = useState(false);
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [toRevoke, setToRevoke] = useState<ApiKey | null>(null);

  if (keys.isPending) return <LoadingBlock />;
  if (keys.isError) return <ErrorBlock error={keys.error} onRetry={() => keys.refetch()} />;

  const newButton = canEdit ? (
    <Button onPress={() => setCreateOpen(true)}>
      <Plus />
      {t("keys.new")}
    </Button>
  ) : null;

  return (
    <div className="flex flex-col gap-6">
      {keys.data.length === 0 ? (
        <Card>
          <EmptyBlock icon={Key} title={t("keys.empty")} hint={t("keys.modeHint")} action={newButton} />
        </Card>
      ) : (
        <>
          <div className="flex justify-end">{newButton}</div>
          <Table>
            <Table.ScrollContainer>
              <Table.Content aria-label={t("appDetail.keys")} className="min-w-[760px]">
                <Table.Header>
                  <Table.Column isRowHeader>{t("common.name")}</Table.Column>
                  <Table.Column>{t("keys.prefix")}</Table.Column>
                  <Table.Column>{t("keys.mode")}</Table.Column>
                  <Table.Column>{t("keys.scopes")}</Table.Column>
                  <Table.Column>{t("keys.lastUsed")}</Table.Column>
                  <Table.Column>{t("common.status")}</Table.Column>
                  <Table.Column className="text-end">{t("common.actions")}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {keys.data.map((k) => (
                    <Table.Row key={k.id} id={k.id}>
                      <Table.Cell className="font-medium">{k.name}</Table.Cell>
                      <Table.Cell>
                        <Code>{k.prefix}…</Code>
                      </Table.Cell>
                      <Table.Cell>
                        <StateChip ok={k.mode === "live" ? true : "warn"} label={t(`keys.${k.mode}`)} />
                      </Table.Cell>
                      <Table.Cell className="text-xs text-muted">{k.scopes.join(", ")}</Table.Cell>
                      <Table.Cell className="text-sm">
                        {k.last_used_at ? fmt.relative(k.last_used_at) : t("common.never")}
                      </Table.Cell>
                      <Table.Cell>
                        {k.revoked_at ? (
                          <StateChip ok={false} label={t("keys.revoked")} />
                        ) : (
                          <StateChip ok label={t("common.active")} />
                        )}
                      </Table.Cell>
                      <Table.Cell className="text-end">
                        {canEdit && !k.revoked_at ? (
                          <Button size="sm" variant="ghost" onPress={() => setToRevoke(k)}>
                            {t("keys.revoke")}
                          </Button>
                        ) : null}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        </>
      )}

      <CreateKeyDialog
        appId={appId}
        isOpen={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={setCreated}
      />

      <InfoDialog
        isOpen={created !== null}
        onOpenChange={(open) => !open && setCreated(null)}
        isDismissable={false}
        title={t("keys.createdTitle")}
      >
        <Alert status="warning">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Description>{t("keys.createdBody")}</Alert.Description>
          </Alert.Content>
        </Alert>
        {created ? <CodeBlock value={created.key} /> : null}
      </InfoDialog>

      <ConfirmDialog
        isOpen={toRevoke !== null}
        onOpenChange={(open) => !open && setToRevoke(null)}
        title={t("keys.revokeTitle")}
        body={t("keys.revokeBody")}
        confirmLabel={t("keys.revoke")}
        isPending={revoke.isPending}
        error={revoke.error}
        onConfirm={() =>
          toRevoke &&
          revoke.mutate(toRevoke.id, {
            onSuccess: () => {
              setToRevoke(null);
              toast.success(t("keys.revokedToast"));
            },
          })
        }
      />
    </div>
  );
}

function CreateKeyDialog({
  appId,
  isOpen,
  onOpenChange,
  onCreated,
}: {
  appId: string;
  isOpen: boolean;
  onOpenChange: (v: boolean) => void;
  onCreated: (key: CreatedApiKey) => void;
}) {
  const { t } = useTranslation();
  const create = useCreateApiKey(appId);
  const [name, setName] = useState("");
  const [mode, setMode] = useState<ApiKeyMode>("live");

  return (
    <FormDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("keys.createTitle")}
      submitLabel={t("common.create")}
      isPending={create.isPending}
      error={create.error}
      onSubmit={() =>
        create.mutate(
          { name, mode },
          {
            onSuccess: (key) => {
              onOpenChange(false);
              setName("");
              setMode("live");
              onCreated(key);
            },
          },
        )
      }
    >
      <TextInput
        name="name"
        label={t("keys.name")}
        placeholder={t("keys.namePlaceholder")}
        value={name}
        onChange={setName}
        autoFocus
        isRequired
      />
      <SelectInput
        label={t("keys.mode")}
        description={t("keys.modeHint")}
        value={mode}
        onChange={(v) => v && setMode(v as ApiKeyMode)}
        options={[
          { id: "live", label: t("keys.live") },
          { id: "test", label: t("keys.test") },
        ]}
      />
    </FormDialog>
  );
}
